/**
 * A workspace to look at, for the dashboard lab: people, projects, and files of the kinds
 * Artwork is made for, each with a picture of its first artboard.
 *
 * Nothing here is fetched. The shape is the shape the real list will need, which is also
 * what the backend has to answer for these designs to be built:
 *
 *   owner        Document.createdById — there today
 *   project      Document.projectId, named by projects-back (`project.list.minimal`) — there today
 *   openedAt     when *I* last opened it — NOT stored yet (Recent needs a per-person row)
 *   sharedWithMe  a file shared with me by name — NOT built yet (no per-file sharing)
 */

import { artSvg, ratioOf, type ArtKind } from '../home/stageArt';

export { artSvg, ratioOf, type ArtKind };

export type Scope = 'recent' | 'mine' | 'shared' | 'workspace';

export interface Person {
  readonly id: string;
  readonly name: string;
  readonly colour: string;
}

export interface Project {
  readonly id: string;
  readonly name: string;
  readonly colour: string;
}


export interface DashFile {
  readonly id: string;
  readonly name: string;
  readonly kind: ArtKind;
  readonly projectId: string | null;
  readonly ownerId: string;
  /** Minutes ago it was last changed by anyone. */
  readonly edited: number;
  /** Minutes ago I last opened it; null when I never have. */
  readonly opened: number | null;
  readonly sharedWithMe: boolean;
  readonly artboards: number;
  readonly palette: readonly [string, string, string, string];
}

export const ME = 'tarek';

export const PEOPLE: readonly Person[] = [
  { id: 'tarek', name: 'Tarek', colour: '#8b5cf6' },
  { id: 'dana', name: 'Dana', colour: '#ec4899' },
  { id: 'farah', name: 'Farah', colour: '#14b8a6' },
  { id: 'walid', name: 'Walid', colour: '#f59e0b' },
  { id: 'mira', name: 'Mira', colour: '#3b82f6' },
];

export const WORKSPACE = { name: 'Flobi Studio', colour: '#8b5cf6' };

export const PROJECTS: readonly Project[] = [
  { id: 'summer', name: 'Summer Campaign', colour: '#f97316' },
  { id: 'brand', name: 'Brand Refresh', colour: '#8b5cf6' },
  { id: 'pitch', name: 'Q4 Pitch', colour: '#0ea5e9' },
  { id: 'olive', name: 'Olive & Co Packaging', colour: '#65a30d' },
];

const P = {
  sun: ['#ff7a45', '#ffd166', '#1d3557', '#fff4e0'],
  sea: ['#0ea5e9', '#e0f2fe', '#0c4a6e', '#fde68a'],
  violet: ['#7c3aed', '#ede9fe', '#1e1b4b', '#f472b6'],
  olive: ['#4d7c0f', '#f7f3e3', '#1a2e05', '#d4a373'],
  night: ['#111827', '#f472b6', '#22d3ee', '#fbbf24'],
  mono: ['#18181b', '#fafafa', '#a1a1aa', '#ef4444'],
  coral: ['#fb7185', '#fff1f2', '#4c0519', '#fcd34d'],
  mint: ['#10b981', '#ecfdf5', '#064e3b', '#f59e0b'],
} as const satisfies Record<string, readonly [string, string, string, string]>;

const f = (id: string, name: string, kind: ArtKind, projectId: string | null, ownerId: string, edited: number, opened: number | null, palette: readonly [string, string, string, string], artboards = 1, sharedWithMe = false): DashFile => ({
  id,
  name,
  kind,
  projectId,
  ownerId,
  edited,
  opened,
  sharedWithMe,
  artboards,
  palette,
});

const H = 60;
const D = 24 * H;

export const FILES: readonly DashFile[] = [
  f('f1', 'Beach Poster', 'poster', 'summer', ME, 12, 12, P.sun, 3),
  f('f2', 'Launch Post — Instagram', 'post', 'summer', 'dana', 40, 55, P.coral, 6, true),
  f('f3', 'Stories Set', 'story', 'summer', ME, 3 * H, 3 * H, P.sea, 5),
  f('f4', 'Logo Explorations', 'logo', 'brand', ME, 5 * H, 2 * H, P.violet, 12),
  f('f5', 'Brand Guidelines Cover', 'poster', 'brand', 'farah', 1 * D, 1 * D + 30, P.violet, 2, true),
  f('f6', 'Business Card', 'card', 'brand', ME, 2 * D, 2 * D, P.mono, 2),
  f('f7', 'Q4 Pitch Deck', 'slide', 'pitch', 'walid', 25, 25, P.night, 14, true),
  f('f8', 'Growth Chart', 'slide', 'pitch', ME, 2 * H, 5 * H, P.sea, 1),
  f('f9', 'Olive Oil Label', 'card', 'olive', 'mira', 3 * D, 3 * D, P.olive, 4, true),
  f('f10', 'Jar Wrap', 'poster', 'olive', 'mira', 4 * D, null, P.olive, 2),
  f('f11', 'Sale Banner', 'post', 'summer', 'dana', 6 * H, null, P.coral, 3),
  f('f12', 'Icon Set', 'logo', 'brand', 'farah', 2 * D, null, P.mint, 24),
  f('f13', 'Event Flyer', 'poster', null, ME, 5 * D, 5 * D, P.night, 1),
  f('f14', 'Wedding Invite', 'card', null, ME, 8 * D, 7 * D, P.coral, 2),
  f('f15', 'Podcast Cover', 'post', null, 'walid', 6 * D, null, P.mono, 1, true),
  f('f16', 'Menu Board', 'poster', 'olive', ME, 9 * D, 9 * D, P.olive, 1),
  f('f17', 'Newsletter Header', 'slide', null, 'mira', 10 * D, null, P.mint, 1),
  f('f18', 'Team Offsite Slides', 'slide', 'pitch', 'walid', 12 * D, null, P.sun, 9),
  f('f19', 'Mascot Sketches', 'logo', null, ME, 14 * D, 13 * D, P.sun, 7),
  f('f20', 'App Store Screens', 'story', 'brand', 'dana', 15 * D, null, P.violet, 6),
  f('f21', 'Sticker Sheet', 'post', null, 'farah', 20 * D, 20 * D, P.mint, 1, true),
  f('f22', 'Tote Bag', 'poster', 'summer', 'mira', 22 * D, null, P.sea, 1),
  f('f23', 'Price Tags', 'card', 'olive', ME, 30 * D, 30 * D, P.olive, 3),
  f('f24', 'Year in Review', 'slide', null, 'walid', 40 * D, null, P.night, 11),
];

export const person = (id: string) => PEOPLE.find((one) => one.id === id)!;
export const project = (id: string | null) => (id ? (PROJECTS.find((one) => one.id === id) ?? null) : null);

export const SCOPES: readonly { id: Scope; label: string; glyph: 'clock' | 'person' | 'people' | 'workspace' }[] = [
  { id: 'recent', label: 'Recents', glyph: 'clock' },
  { id: 'mine', label: 'Owned by Me', glyph: 'person' },
  { id: 'shared', label: 'Shared with Me', glyph: 'people' },
  { id: 'workspace', label: 'Workspace', glyph: 'workspace' },
];

/** The files a scope holds, in the order it shows them. */
export function filesIn(scope: Scope, options: { projectId?: string | null; search?: string } = {}): DashFile[] {
  let list = [...FILES];
  if (scope === 'recent') list = list.filter((file) => file.opened !== null).sort((a, b) => a.opened! - b.opened!);
  else {
    if (scope === 'mine') list = list.filter((file) => file.ownerId === ME);
    if (scope === 'shared') list = list.filter((file) => file.sharedWithMe);
    list.sort((a, b) => a.edited - b.edited);
  }
  if (options.projectId !== undefined) list = list.filter((file) => file.projectId === options.projectId);
  const search = options.search?.trim().toLowerCase();
  if (search) list = list.filter((file) => file.name.toLowerCase().includes(search) || (project(file.projectId)?.name.toLowerCase().includes(search) ?? false));
  return list;
}

export const countIn = (scope: Scope) => filesIn(scope).length;
export const filesOfProject = (id: string | null) => FILES.filter((file) => file.projectId === id).sort((a, b) => a.edited - b.edited);

/** "12 minutes ago", "Yesterday", "3 Sept": how long ago, the way Finder says it. */
export function when(minutes: number): string {
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) {
    const hours = Math.round(minutes / 60);
    return `${hours} hr ago`;
  }
  const days = Math.round(minutes / (24 * 60));
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  const date = new Date(Date.UTC(2026, 8, 29) - minutes * 60_000);
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
